import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconUserCircle, IconMapPin, IconQuestionMark, IconBuilding, IconMap, IconInfoCircle, IconFolder, IconRepeat } from '@tabler/icons-react';
import Tooltip from '@mui/material/Tooltip';
import useUserProfile from '../../../hooks/useUserProfile';

import { formatDistanceToNow } from 'date-fns';
import { ACTION_TAGS } from '../PostBox/constants/actionTags';
import { CORE_ISSUES } from '../PostBox/constants/coreIssues';
import { ISSUE_STATUSES } from '../PostBox/constants/issueStatuses';
import { actionTagIcons, issueIcons } from '../PostBox/constants/iconMappings';
import { SCALE_OPTIONS } from '../PostBox/constants/scaleOptions';
import { parseLocation } from '../../../utils/locationUtils';
import ScaleSilhouette from '../../../components/common/ScaleSilhouette';
import { resolveCountryCodeFromLocation } from '../../../utils/countryCodeUtils';

// Using shared ScaleSilhouette component to render silhouettes consistently

const getActionTagId = (tagObj) => typeof tagObj === 'object' && tagObj !== null ? tagObj.id : tagObj;

const getActionTagData = (tagObj) => {
    const id = getActionTagId(tagObj);
    const tag = ACTION_TAGS.find(t => t.id === id);
    return tag || { id: 'default', label: 'General', color: '#95a5a6' };
};

const getActionTagIcon = (tagObj, size) => {
    const id = getActionTagId(tagObj);
    const icon = actionTagIcons[id];
    if (icon) {
        return React.cloneElement(icon, { size });
    }
    return null;
};

const getIssueLabel = (issueObj) => {
    if (typeof issueObj === 'object' && issueObj !== null) {
        return issueObj.name || issueObj.label || issueObj.id || '';
    }
    return CORE_ISSUES.find(i => i.id === issueObj)?.name || issueObj || '';
};

const getIssueIcon = (id) => issueIcons[id] || null;

const getScaleLabel = (id) => SCALE_OPTIONS.find(s => s.id === id)?.label || id;
const getIssueStatus = (id) => ISSUE_STATUSES.find(s => s.id === id);

// getIssueStatusIcon removed - currently unused

const formatTimestamp = (timestamp) => {
    if (!timestamp) {
        return 'just now';
    }

    if (typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
        return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
    }

    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
        return 'just now';
    }

    return formatDistanceToNow(date, { addSuffix: true });
};;

const PlaceholderBadge = ({ label }) => (
    <div className="post__badge-base badge--placeholder" title={label}>
        <div className="post__badge-content">
            <span className="post__badge-icon">
                <IconQuestionMark size={16} />
            </span>
            <span className="post__badge-label">{label}</span>
        </div>
    </div>
);

export default function PostHeader({
    username,
    profession,
    timestamp,
    avatar,
    actionTag,
    issue,
    scale,
    location,
    issueStatus,
    postAuthorId,
    hasProject = false,
    projectId = null,
    type = null,          // Issue #7: Used to detect shared posts
    sharedPostId = null,  // Issue #7: ID of the original shared post
    hideSettings = false, // Disable extra options
    isInsideReshare = false, // Flag for modal previews
    currentUser = null    // Current logged-in user (for "You shared this" check)
}) {
    const [showTooltip, setShowTooltip] = useState(false);
    const { profile } = useUserProfile(postAuthorId);
    const currentActionTag = getActionTagData(actionTag);
    const ActionTagIcon = getActionTagIcon(actionTag, 13);
    const navigate = useNavigate();

    const handleProfileClick = () => {
        if (isInsideReshare) return; // Disable navigation in previews
        if (postAuthorId) {
            navigate(`/profile/${postAuthorId}`);
        } else {
            console.warn('Cannot navigate to profile: postAuthorId is undefined for this post.');
        }
    };

    const parsedLocation = parseLocation(location);
    const compactParsedLocation = [parsedLocation.city, parsedLocation.region, parsedLocation.country]
        .filter(Boolean)
        .join(', ');
    const safeLocationLabel = (typeof location === 'object' && location !== null)
        ? (location.name || '')
        : (location || '');
    const displayLocation = safeLocationLabel || compactParsedLocation;
    const threeLetterCountryCode = useMemo(() => resolveCountryCodeFromLocation(location), [location]);

    // Resolve display name from users cache with sensible fallbacks
    const displayUsername = useMemo(() => {
        const looksLikeEmail = (v) => typeof v === 'string' && v.includes('@');
        const base = username;
        const profName = profile?.username || profile?.displayName || profile?.name;

        // Always prioritize profile data over stored "Anonymous" or email-like usernames
        if (profName && (!base || base === 'Anonymous' || looksLikeEmail(base))) {
            return profName;
        }

        // If no profile data and stored username is problematic, create a better fallback
        if (!base || base === 'Anonymous' || looksLikeEmail(base)) {
            return 'User';
        }

        return base;
    }, [username, profile]);

    // Resolve avatar from profile data if available, fallback to post data
    const displayAvatar = useMemo(() => {
        // Debug logging to understand what's happening
        if (process.env.NODE_ENV === 'development') {
            console.debug('[PostHeader] Avatar Debug:', {
                postAuthorId,
                avatar,
                'profile?.photoURL': profile?.photoURL,
                'profile?.userPhotoURL': profile?.userPhotoURL,
                'profile exists': !!profile
            });
        }

        // Priority: profile photoURL > post userPhotoURL > null (shows IconUserCircle)
        const profilePhoto = profile?.photoURL || profile?.userPhotoURL;
        if (profilePhoto && profilePhoto !== 'null' && profilePhoto !== 'undefined' && profilePhoto.trim() !== '') {
            return profilePhoto;
        }

        // Fallback to post data
        if (avatar && avatar !== 'null' && avatar !== 'undefined' && avatar.trim() !== '') {
            return avatar;
        }

        return null;
    }, [avatar, profile, postAuthorId]);

    const renderMap = () => {
        const mapProps = {
            onMouseEnter: () => setShowTooltip(true),
            onMouseLeave: () => setShowTooltip(false),
            onClick: () => setShowTooltip(!showTooltip),
            className: "scale-icon-wrapper"
        };

        if (scale === 'global') {
            return (
                <Tooltip
                    title="Global"
                    arrow
                    open={showTooltip && scale === 'global'}
                    onClose={() => setShowTooltip(false)}
                    disableFocusListener
                    disableTouchListener
                >
                    <div {...mapProps}>
                        <ScaleSilhouette scale="global" location="Global" className="global-map-icon" />
                        <IconInfoCircle
                            size={14}
                            style={{
                                marginLeft: '4px',
                                color: '#666',
                                alignSelf: 'flex-end',
                                marginBottom: '2px'
                            }}
                        />
                    </div>
                </Tooltip>
            );
        }
        if (scale === 'national' && threeLetterCountryCode) {
            return (
                <Tooltip
                    title="National"
                    arrow
                    open={showTooltip && scale === 'national'}
                    onClose={() => setShowTooltip(false)}
                    disableFocusListener
                    disableTouchListener
                >
                    <div {...mapProps}>
                        <ScaleSilhouette
                            scale="national"
                            location={location}
                            className="map-silhouette-icon"
                        />
                        <IconInfoCircle
                            size={14}
                            style={{
                                marginLeft: '4px',
                                color: '#666',
                                alignSelf: 'flex-end',
                                marginBottom: '2px'
                            }}
                        />
                    </div>
                </Tooltip>
            );
        }
        return null;
    };

    const renderScaleIcon = () => {
        let icon = null;
        let tooltipText = '';

        if (scale === 'state' || scale === 'regional') {
            icon = <IconMap size={28} className="scale-icon" style={{ color: '#e67e22' }} />;
            tooltipText = 'State/Regional';
        } else if (scale === 'local' || ((scale === 'state' || scale === 'regional') && parsedLocation?.city)) {
            icon = <IconBuilding size={28} className="scale-icon" style={{ color: '#95a5a6' }} />;
            tooltipText = 'Local';
        } else if (scale === 'global') {
            tooltipText = 'Global';
        } else if (scale === 'national') {
            tooltipText = 'National';
        }

        return icon ? (
            <Tooltip
                title={tooltipText}
                arrow
                open={showTooltip}
                onClose={() => setShowTooltip(false)}
                disableFocusListener
                disableTouchListener
            >
                <div
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onClick={() => setShowTooltip(!showTooltip)}
                    style={{ display: 'inline-flex', cursor: 'help' }}
                >
                    {icon}
                    <IconInfoCircle
                        size={14}
                        style={{
                            marginLeft: '4px',
                            color: '#666',
                            alignSelf: 'flex-end',
                            marginBottom: '2px'
                        }}
                    />
                </div>
            </Tooltip>
        ) : null;
    };

    return (
        <div className="post__header">
            <div className="post__avatar" onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
                {displayAvatar ? (
                    <img
                        src={displayAvatar}
                        alt={displayUsername}
                        className="avatar-image"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                        }}
                    />
                ) : null}
                <IconUserCircle
                    size={48}
                    style={{
                        display: displayAvatar ? 'none' : 'block',
                        color: '#666'
                    }}
                />
            </div>

            <div className="post__body">
                <div className="post__header-left">
                    <div className="post__headerText">
                        <h3 onClick={handleProfileClick} style={{ cursor: 'pointer' }}>{displayUsername || username || 'User'}</h3>
                        <span className="post__timestamp">
                            • {formatTimestamp(timestamp)}
                        </span>
                        {/* Mobile reshare badge - visible on mobile screens */}
                        {(type === 'shared_post' || actionTag === 'share') && (
                            <span className="post__mobile-reshare-badge">
                                <IconRepeat size={10} />
                                <span>{currentUser && currentUser.uid === postAuthorId ? 'You shared this' : 'Reshared'}</span>
                            </span>
                        )}
                    </div>
                    <div className="post__headerDescription">
                        <h4>{profession}</h4>
                        {/* Reshared badge shown alongside action tag */}
                        {(type === 'shared_post' || actionTag === 'share') && (
                            <div className="post-header-badge reshared" style={{ marginLeft: 'auto' }}>
                                <IconRepeat size={14} />
                                <span>{currentUser && currentUser.uid === postAuthorId ? 'You shared this' : 'Reshared'}</span>
                            </div>
                        )}
                        {actionTag !== 'share' && actionTag && (
                            <div
                                className="post-action-tag"
                                style={{
                                    color: currentActionTag.color,
                                    borderColor: currentActionTag.color,
                                }}
                            >
                                {ActionTagIcon && <span className="post-action-tag-icon" style={{ marginRight: '5px', display: 'flex', alignItems: 'center' }}>{ActionTagIcon}</span>}
                                <span>{currentActionTag.label}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Header right: always show badges (map, location, issue, status) */}
            <div className="post__header-right">
                {!hideSettings && (
                    <div className="map-silhouette-wrapper">
                        <div className="map-silhouette-fixed-width-wrapper">
                            <div className="scale-icon-container">
                                {scale === 'global' || scale === 'national' ? renderMap() : renderScaleIcon()}
                            </div>
                        </div>
                    </div>
                )}
                <div className="post__badges-wrapper">
                    {hasProject && projectId ? (
                        <div
                            className="post__badge-base badge--project"
                            title="Project"
                            onClick={() => navigate(`/projects/${projectId}`)}
                            style={{ cursor: 'pointer' }}
                        >
                            <div className="post__badge-content">
                                <span className="post__badge-icon"><IconFolder size={14} /></span>
                                <span className="post__badge-label">Project</span>
                            </div>
                        </div>
                    ) : null}
                        {scale || displayLocation ? (
                            <div
                                className="post__badge-base badge--location"
                                title={displayLocation ? `${displayLocation}${scale && scale !== 'global' ? ` (${getScaleLabel(scale)})` : ''}` : getScaleLabel(scale)}
                            >
                                <div className="post__badge-content">
                                    <span className="post__badge-icon">
                                        <IconMapPin size={14} />
                                    </span>
                                    <span className="post__badge-label">
                                        {displayLocation ? (
                                            <>
                                                {displayLocation}
                                                {scale && scale !== 'global' && ` (${getScaleLabel(scale)})`}
                                            </>
                                        ) : (
                                            getScaleLabel(scale)
                                        )}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <PlaceholderBadge label="No Location" />
                        )}
                        {issue ? (
                            <div className="post__badge-base badge--issue" title={getIssueLabel(issue)}>
                                <div className="post__badge-content">
                                    <span className="post__badge-icon">{getIssueIcon(issue?.id || issue)}</span>
                                    <span className="post__badge-label">{getIssueLabel(issue)}</span>
                                </div>
                            </div>
                        ) : (
                            <PlaceholderBadge label="No Issue" />
                        )}
                        {issueStatus ? (
                            <div
                                className="post__badge-base badge--status"
                            >
                                <div className="post__badge-content">
                                    <span className="post__badge-label">Status: {getIssueStatus(issueStatus)?.label}</span>
                                </div>
                            </div>
                        ) : (
                            <PlaceholderBadge label="No Status" />
                        )}
                    </div>
                </div>
        </div>
    );
}